import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Zap, DollarSign, Shield, TrendingUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const features = [
  {
    icon: Zap,
    title: 'Fast Transactions',
    description: 'Process thousands of transactions per second with near-instant finality.',
  },
  {
    icon: DollarSign,
    title: 'Low Cost',
    description: 'Minimal gas fees make micro-transactions and DeFi accessible to everyone.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Trusted validators ensure network integrity and Byzantine fault tolerance.',
  },
  {
    icon: TrendingUp,
    title: 'Scalable',
    description: 'Built to scale horizontally as network demand grows exponentially.',
  },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="about"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
              About Polk Chain
            </span>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Proof of Authority{' '}
              <span className="gradient-text">Reimagined</span>
            </h2>
            <p className={`text-base sm:text-lg leading-relaxed mb-8 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
              Polk Chain leverages a next-generation Proof of Authority consensus mechanism,
              combining the speed of centralized systems with the security guarantees of
              decentralized networks. Our vetted validator system ensures rapid block
              finalization while maintaining trustless verification.
            </p>

            {/* Feature Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {feature.title}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: PoA Diagram */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/e8f5391f-27da-4e56-b4ea-a3f7a756238b/images/3ffb82e7db55c903d2ccec67fc7b339de538cbc0cfdfeefa41f4a389f3e5ffd2.png"
                alt="Proof of Authority Concept"
                className="w-full h-auto rounded-3xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>

            {/* Floating badges */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className={`absolute -top-4 -right-4 sm:top-4 sm:right-4 px-4 py-2 rounded-full backdrop-blur-md shadow-lg ${
                isDark 
                  ? 'bg-white/10 border border-white/20' 
                  : 'bg-white/90 border border-black/10'
              }`}
            >
              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Consensus Time: &lt;1s
              </span>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 3.5, repeat: Infinity }}
              className={`absolute bottom-4 -left-4 sm:bottom-8 sm:left-4 px-4 py-2 rounded-full backdrop-blur-md shadow-lg ${
                isDark 
                  ? 'bg-white/10 border border-white/20' 
                  : 'bg-white/90 border border-black/10'
              }`}
            >
              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                21 Validators
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
